<template>
  <v-container fluid>
    <v-card>
      <v-toolbar flat color="primary" dark>
        <v-toolbar-title>Audios</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="getAllAudios">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search by user ID, email, or file name"
              single-line
              hide-details
              clearable
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Now Playing Bar -->
        <v-card
          v-if="playingId"
          outlined
          class="mt-4 pa-3 now-playing-bar"
        >
          <v-row align="center" no-gutters>
            <v-col cols="auto" class="mr-3">
              <v-btn icon color="primary" @click="togglePlay(nowPlayingItem)">
                <v-icon>mdi-pause</v-icon>
              </v-btn>
            </v-col>
            <v-col>
              <div class="now-playing-info">
                <div class="caption grey--text">Now Playing</div>
                <div class="body-2 font-weight-medium">
                  {{ nowPlayingItem ? nowPlayingItem.email || nowPlayingItem.userId : "" }}
                </div>
                <div class="caption grey--text text--darken-1">
                  {{ nowPlayingItem ? getFileName(nowPlayingItem.fileKey) : "" }}
                </div>
              </div>
            </v-col>
            <v-col cols="auto" class="mr-3">
              <span class="caption font-weight-medium">{{ currentTimeDisplay }} / {{ totalDurationDisplay }}</span>
            </v-col>
            <v-col cols="4" class="px-2">
              <v-progress-linear
                :value="playProgress"
                color="primary"
                height="6"
                rounded
                class="progress-bar"
              ></v-progress-linear>
            </v-col>
            <v-col cols="auto" class="ml-2">
              <v-btn icon small @click="stopPlayback">
                <v-icon small>mdi-stop</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <v-data-table
          :headers="headers"
          :items="audios"
          :search="search"
          :loading="loading"
          loading-text="Loading audios..."
          class="elevation-1 mt-4"
        >
          <template v-slot:item.play="{ item }">
            <v-btn
              icon
              :color="playingId === item._id ? 'primary' : 'grey darken-1'"
              :loading="loadingAudioId === item._id"
              :disabled="loadingAudioId && loadingAudioId !== item._id"
              @click="togglePlay(item)"
            >
              <v-icon>
                {{ playingId === item._id ? "mdi-pause" : "mdi-play" }}
              </v-icon>
            </v-btn>
          </template>

          <template v-slot:item.userId="{ item }">
            <v-btn
              text
              small
              color="primary"
              class="px-0"
              @click="goToUserProfile(item.userId)"
            >
              {{ item.userId }}
            </v-btn>
          </template>

          <template v-slot:item.fileName="{ item }">
            <span class="file-name" :title="item.fileKey">
              {{ getFileName(item.fileKey) }}
            </span>
          </template>

          <template v-slot:item.durationMs="{ item }">
            {{ formatDuration(item.durationMs) }}
          </template>

          <template v-slot:item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import RestResource from "../../../services/dataServiceMobileV2";
const service = new RestResource();

const URL_TTL_MS = 4 * 60 * 1000;

export default {
  data() {
    return {
      audios: [],
      search: "",
      loading: false,
      playingId: null,
      loadingAudioId: null,
      urlCache: {},
      currentAudio: null,
      nowPlayingItem: null,
      playProgress: 0,
      currentTimeDisplay: "0:00",
      totalDurationDisplay: "0:00",
      progressInterval: null,
      headers: [
        { text: "Play", value: "play", sortable: false, width: "80px" },
        { text: "User ID", value: "userId" },
        { text: "Email", value: "email" },
        { text: "File", value: "fileName" },
        { text: "Duration", value: "durationMs" },
        { text: "Created At", value: "createdAt" },
      ],
    };
  },

  mounted() {
    this.getAllAudios();
  },

  beforeDestroy() {
    this.stopPlayback();
  },

  methods: {
    async getAllAudios() {
      this.loading = true;
      this.$setLoader();
      try {
        const response = await service.getAllAudios();
        this.audios = response.data.data || response.data || [];
      } finally {
        this.loading = false;
        this.$disableLoader();
      }
    },

    async togglePlay(item) {
      if (this.playingId === item._id) {
        this.stopPlayback();
        return;
      }

      if (this.playingId) {
        this.stopPlayback();
      }

      this.loadingAudioId = item._id;

      try {
        const url = await this.fetchPlaybackUrl(item);

        const audio = document.createElement("audio");
        const types = ["audio/mp4", "audio/aac", "audio/x-m4a", "audio/mpeg"];
        types.forEach((type) => {
          const source = document.createElement("source");
          source.src = url;
          source.type = type;
          audio.appendChild(source);
        });

        audio.onended = () => {
          this.clearProgress();
          this.playingId = null;
          this.currentAudio = null;
          this.nowPlayingItem = null;
          this.playProgress = 0;
          this.currentTimeDisplay = "0:00";
          this.totalDurationDisplay = "0:00";
        };

        audio.onerror = () => {
          this.clearProgress();
          this.playingId = null;
          this.loadingAudioId = null;
          this.currentAudio = null;
          this.nowPlayingItem = null;
        };

        this.currentAudio = audio;
        this.nowPlayingItem = item;
        this.playingId = item._id;
        this.loadingAudioId = null;
        this.totalDurationDisplay = this.formatDuration(item.durationMs);
        audio.load();
        audio.play();
        this.startProgress(item.durationMs);
      } catch (error) {
        this.playingId = null;
        this.loadingAudioId = null;
        this.$showSuccessMessage({
          message: "Unable to play this recording.",
        });
      }
    },

    startProgress(durationMs) {
      this.clearProgress();
      this.progressInterval = setInterval(() => {
        if (this.currentAudio && !this.currentAudio.paused) {
          const currentMs = this.currentAudio.currentTime * 1000;
          const total = durationMs || 1;
          this.playProgress = Math.min((currentMs / total) * 100, 100);
          this.currentTimeDisplay = this.formatDuration(currentMs);
        }
      }, 200);
    },

    clearProgress() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    },

    async fetchPlaybackUrl(item) {
      const cached = this.urlCache[item._id];
      if (cached && Date.now() - cached.cachedAt < URL_TTL_MS) {
        return cached.url;
      }

      const response = await service.getPlaybackUrl(item.fileKey);
      const playbackUrl =
        response.data?.playbackUrl ?? response.data?.data?.playbackUrl;

      if (!playbackUrl) {
        throw new Error("Could not get playback URL");
      }

      this.$set(this.urlCache, item._id, {
        url: playbackUrl,
        cachedAt: Date.now(),
      });

      return playbackUrl;
    },

    stopPlayback() {
      this.clearProgress();
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio = null;
      }
      this.playingId = null;
      this.nowPlayingItem = null;
      this.playProgress = 0;
      this.currentTimeDisplay = "0:00";
      this.totalDurationDisplay = "0:00";
    },

    getFileName(fileKey) {
      if (!fileKey) {
        return "-";
      }
      const parts = fileKey.split("/");
      return parts[parts.length - 1];
    },

    formatDuration(durationMs) {
      if (!durationMs && durationMs !== 0) {
        return "-";
      }
      const totalSeconds = Math.floor(durationMs / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${String(seconds).padStart(2, "0")}`;
    },

    formatDate(date) {
      if (!date) {
        return "-";
      }
      return new Date(date).toLocaleString();
    },

    goToUserProfile(userId) {
      this.$router.push({
        name: "MobileV2UserProfile",
        params: { userId },
      });
    },
  },
};
</script>

<style scoped>
.file-name {
  font-family: monospace;
  font-size: 13px;
  word-break: break-all;
}

.now-playing-bar {
  border-color: #1976d2 !important;
  background: #e3f2fd;
}

.progress-bar {
  cursor: pointer;
}
</style>